import React from "react";
import { GHARS, CELL_TYPES, GHAR_TYPE_HI, isDefaultArea } from "../../data/rssbData";
import { Users, RotateCw, Scale, Disc, Ban, PencilRuler, Phone, Unlock, UserCheck, MapPin } from "lucide-react";

const RULES = [
  { icon: UserCheck, title: "हर सेवादार को सेवा", desc: "हर SR/SK और हर पाठी को माह में कम से कम 1 सेवा अवश्य मिलती है — कोई भी पूरे माह बिना सेवा नहीं रहता।" },
  { icon: Unlock, title: "रीडर हर जगह", desc: "SR/SK किसी भी सत्संग घर में सेवा दे सकते हैं।" },
  { paliaOnly: true, icon: MapPin, title: "पाठी स्थान नियम", desc: "राम आशीष, तृप्ता — माझरा पश्चिम, बामनगर, पलिया कलां। कुलवंत कौर, राज कौर, साधना देवी, हरबंस सिंह, सुबेग सिंह, जीत सिंह, राजेंद्र सिंह — गांधी नगर, राघवपुरी, टाटरगंज। हरपाल — बामनगर (विशेष सत्संग में प्राथमिकता)।" },
  { paliaOnly: true, icon: MapPin, title: "बनकटी क्षेत्र की महिला पाठी", desc: "बनकटी, चंदन चौकी, देवरही, बरबटा की महिला पाठी केवल अपने सत्संग घर में। अनीता व सोमवती को इन चारों घरों के विशेष सत्संग में प्राथमिकता।" },
  { icon: Ban, title: "एक दिन दोहरी ड्यूटी नहीं", desc: "एक व्यक्ति को एक ही तारीख को दो अलग सत्संग घरों में नियुक्त नहीं किया जाएगा।" },
  { icon: RotateCw, title: "रोटेशन नियम", desc: "एक ही SR/SK को माह में उसी सत्संग घर में दोबारा नियुक्त नहीं किया जाता; पिछले 3 महीनों की जोड़ियाँ भी टाली जाती हैं।" },
  { icon: Scale, title: "हर सत्संग में SK + पाठी", desc: "हर सत्संग सेल में सत्संग कर्ता और पाठी दोनों अवश्य नियुक्त होते हैं — कोई सेल खाली या आधा नहीं रहता।" },
  { icon: Users, title: "विशेष सत्संग कर्ता", desc: "बाहर से आने वाले विशेष SK की एंट्री लॉक रहती हैं। बाकी शेड्यूल उन्हीं के इर्द-गिर्द बनता है।" },
  { icon: Disc, title: "CD / सवाल-जवाब नियत", desc: "हर सत्संग घर में माह में 1 CD सत्संग और 1 सवाल-जवाब (LCD) सत्संग अवश्य नियत होता है।" },
  { icon: PencilRuler, title: "मैनुअल शीट", desc: "'मैनुअल शीट' से खाली शीट बनाकर हर सेल में स्वयं प्रचारक/पाठी चुन या टाइप कर सकते हैं।" },
  { icon: Phone, title: "नाम के साथ स्थान व मोबाइल", desc: "हर नाम के आगे कोष्ठक में स्थान/लोकैलिटी और (यदि चाहें तो) मोबाइल नंबर जोड़ा जा सकता है — दोनों वैकल्पिक।" },
];

export const RulesTab = () => (
  <div className="space-y-8">
    <section>
      <h2 className="font-serif-dev text-xl font-bold text-[#3a1212] mb-4">शेड्यूलिंग नियम / Scheduling Rules</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {RULES.filter((r) => isDefaultArea || !r.paliaOnly).map((r, i) => (
          <div
            key={i}
            data-testid={`rule-${i}`}
            className="animate-fade-up bg-[#fffdf7] border border-[#e3d6b6] rounded-xl p-4 hover:border-[#6e1b1b]/50 transition-colors"
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <div className="w-9 h-9 rounded-lg grid place-items-center bg-[#e3d6b6] text-[#6e1b1b] mb-2">
              <r.icon className="w-5 h-5" />
            </div>
            <h3 className="font-serif-dev font-semibold text-[#3a1212] text-sm">{r.title}</h3>
            <p className="text-xs text-[#7a4a3a] mt-1 leading-relaxed">{r.desc}</p>
          </div>
        ))}
      </div>
    </section>

    <section>
      <h2 className="font-serif-dev text-xl font-bold text-[#3a1212] mb-4">सभी {GHARS.length} सत्संग घर</h2>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {GHARS.map((g, i) => (
          <div key={g.id} data-testid={`ghar-${g.id}`} className="flex items-center justify-between bg-[#fffdf7] border border-[#e3d6b6] rounded-lg px-3 py-2">
            <span className="text-[#3a1212] font-dev text-sm">
              <span className="text-[#9a7a5a] mr-1.5">{i + 1}.</span>
              {g.hi}
            </span>
            <span className="text-[10px] text-[#6e1b1b] border border-[#6e1b1b]/40 rounded px-1.5">{GHAR_TYPE_HI[g.type]}</span>
          </div>
        ))}
      </div>
    </section>

    <section>
      <h2 className="font-serif-dev text-xl font-bold text-[#3a1212] mb-4">संकेत / Legend</h2>
      <div className="flex flex-wrap gap-3">
        {Object.entries(CELL_TYPES).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 bg-[#fffdf7] border border-[#e3d6b6] rounded-lg px-3 py-2 text-sm">
            <span className="text-[#6e1b1b] font-semibold">{v.labelHi}</span>
            <span className="text-[#7a4a3a]">= {v.full}</span>
          </div>
        ))}
      </div>
    </section>
  </div>
);
